import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  IconButton,
  FormHelperText,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import companyPlanService, { CompanyPlan } from '../../services/companyPlan.service';
import planService, { Plan } from '../../services/plan.service';
import { addMonths, addDays } from 'date-fns';

const paymentMethods = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'invoice', label: 'Invoice' },
];

const AddPlan: React.FC = () => {
  const { id: companyId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  // Form state
  const [formData, setFormData] = useState<Partial<CompanyPlan>>({
    plan: '',
    startDate: new Date(),
    endDate: addDays(new Date(), 30),
    isActive: true,
    paymentStatus: 'pending',
    paymentMethod: undefined,
    transactionId: undefined,
    notes: undefined,
  });

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [plansLoading, setPlansLoading] = useState<boolean>(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  
  // Validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch available plans
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await planService.getPlans({ status: 'active' });
      setPlans(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching plans:', err);
      setError('Failed to load available plans. Please try again.');
    } finally {
      setPlansLoading(false);
    }
  };

  // Find selected plan details
  const selectedPlan = plans.find(plan => plan._id === formData.plan);

  // Update end date when plan or start date changes
  useEffect(() => {
    if (selectedPlan && formData.startDate) {
      setFormData(prev => ({
        ...prev,
        endDate: addDays(new Date(formData.startDate!), selectedPlan.duration),
      }));
    }
  }, [formData.plan, formData.startDate, selectedPlan]);

  // Handle form input changes
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    
    if (name) {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // Clear error for this field if it exists
      if (formErrors[name]) {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // Handle date changes
  const handleDateChange = (date: Date | null, field: 'startDate' | 'endDate') => {
    if (date) {
      setFormData(prev => ({ ...prev, [field]: date }));
      
      // Clear error for this field if it exists
      if (formErrors[field]) {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.plan) {
      errors.plan = 'Plan is required';
    }
    
    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (!formData.endDate) {
      errors.endDate = 'End date is required';
    } else if (formData.startDate && formData.endDate < formData.startDate) {
      errors.endDate = 'End date must be after start date';
    }
    
    if (!formData.paymentStatus) {
      errors.paymentStatus = 'Payment status is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!companyId) {
      setError('Company ID is missing');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await companyPlanService.createCompanyPlan(companyId, formData);
      setSuccess(true);
      
      // Redirect back to company details page after success
      setTimeout(() => {
        navigate(`/companies/${companyId}`);
      }, 1500);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create company plan');
      console.error('Error creating company plan:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate(`/companies/${companyId}`)} 
          sx={{ mr: 1 }}
          aria-label="Back to company details"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Add New Plan
        </Typography>
      </Box>
      
      {/* Error/Success message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Plan created successfully!
        </Alert>
      )}
      
      {/* Plan form */}
      <Paper 
        elevation={3}
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          mb: 4
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Plan Selection */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Select Plan
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <FormControl fullWidth error={!!formErrors.plan}>
                <InputLabel>Plan</InputLabel>
                <Select
                  name="plan"
                  value={formData.plan}
                  onChange={handleChange}
                  label="Plan"
                  disabled={plansLoading}
                >
                  {plansLoading ? (
                    <MenuItem value="" disabled>
                      Loading plans...
                    </MenuItem>
                  ) : plans.length === 0 ? (
                    <MenuItem value="" disabled>
                      No active plans available
                    </MenuItem>
                  ) : (
                    plans.map((plan) => (
                      <MenuItem key={plan._id} value={plan._id}>
                        {plan.name} - ${plan.price}/{plan.duration} days
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.plan && <FormHelperText>{formErrors.plan}</FormHelperText>}
              </FormControl>
              
              {selectedPlan && (
                <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.light, 0.1), borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {selectedPlan.name} Plan Details
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedPlan.description}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" fontWeight="bold">Features:</Typography>
                      <ul style={{ marginTop: 0 }}>
                        {selectedPlan.features.map((feature, index) => (
                          <li key={index}>
                            <Typography variant="body2">{feature}</Typography>
                          </li>
                        ))}
                      </ul>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Price:</strong> ${selectedPlan.price}/month
                      </Typography>
                      <Typography variant="body2">
                        <strong>Max Projects:</strong> {selectedPlan.maxProjects}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Max Users:</strong> {selectedPlan.maxUsers}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Duration:</strong> {selectedPlan.duration} days
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Grid>
            
            {/* Dates and Status */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Subscription Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(date) => handleDateChange(date, 'startDate')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formErrors.startDate,
                          helperText: formErrors.startDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={formData.endDate}
                      onChange={(date) => handleDateChange(date, 'endDate')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formErrors.endDate,
                          helperText: formErrors.endDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.paymentStatus}>
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      name="paymentStatus"
                      value={formData.paymentStatus}
                      onChange={handleChange}
                      label="Payment Status"
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                    {formErrors.paymentStatus && <FormHelperText>{formErrors.paymentStatus}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      name="paymentMethod"
                      value={formData.paymentMethod || ''}
                      onChange={handleChange}
                      label="Payment Method"
                    >
                      {paymentMethods.map((method) => (
                        <MenuItem key={method.value} value={method.value}>
                          {method.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Additional Information */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Transaction ID"
                    name="transactionId"
                    value={formData.transactionId || ''}
                    onChange={handleChange}
                    fullWidth
                    placeholder="Optional transaction reference"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    name="notes"
                    value={formData.notes || ''}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Additional notes about this plan subscription"
                  />
                </Grid>
              </Grid>
            </Grid>
            
            {/* Form Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate(`/companies/${companyId}`)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                >
                  {loading ? 'Creating...' : 'Assign Plan'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AddPlan;